import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import { api } from '@/lib/api'

// ===================
// Types
// ===================

export type CollaborationStatus =
  | 'not_started'
  | 'in_progress'
  | 'draft_complete'
  | 'in_review'
  | 'changes_requested'
  | 'approved'
  | 'complete'

export interface ReviewRound {
  reviewerId: string
  reviewerName?: string
  /** 'withdrawn' is written when the section leaves In Review without a decision. */
  decision: 'pending' | 'approved' | 'changes_requested' | 'withdrawn'
  comment: string | null
  createdAt: string
  decidedAt?: string
  /**
   * Spec 29 §7.3. Set on a `changes_requested` round when the reviewer ticked
   * "Require another review before completion": the writer cannot mark the
   * section Complete until it has been back through review.
   */
  requireReReview?: boolean
}

export interface SectionAssignment {
  id: string
  bidId: string
  sectionId: string
  userId: string | null
  role: 'writer' | 'reviewer'
  assignedBy: string | null
  collaborationStatus: CollaborationStatus
  reviewerId: string | null
  reviewRounds: ReviewRound[]
  createdAt: string
  /** When collaborationStatus last changed — drives the "Completed by … · date" provenance line. */
  collaborationStatusChangedAt: string
  email: string | null
  fullName: string | null
  avatarUrl: string | null
  /**
   * Who is planned to review the section — a plan set from the outline table,
   * not a review request (no notification, no round). `reviewerId` is the
   * requested reviewer and wins when set. Optional: a server without migration
   * 181 omits it.
   */
  plannedReviewerId?: string | null
  plannedReviewerName?: string | null
}

interface SectionAssignmentsState {
  // State
  assignments: SectionAssignment[]
  isLoading: boolean
  error: string | null
  currentArtifactId: string | null

  // Actions
  fetchAssignments: (artifactId: string) => Promise<void>
  assignSection: (artifactId: string, sectionKey: string, userId: string) => Promise<void>
  unassignSection: (artifactId: string, sectionKey: string) => Promise<void>
  /** Plan (or clear, with null) the section's reviewer. Quiet: no notification, no status change. */
  planReviewer: (artifactId: string, sectionKey: string, userId: string | null) => Promise<void>
  updateCollaborationStatus: (artifactId: string, sectionKey: string, status: CollaborationStatus) => Promise<void>
  requestReview: (artifactId: string, sectionKey: string, reviewerId: string) => Promise<void>
  submitReview: (artifactId: string, sectionKey: string, decision: 'approved' | 'changes_requested', comment?: string, options?: { requireReReview?: boolean }) => Promise<void>
  getAssignment: (sectionKey: string) => SectionAssignment | undefined
  reset: () => void
}

// ===================
// Store
// ===================

export const useSectionAssignmentsStore = create<SectionAssignmentsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      assignments: [],
      isLoading: false,
      error: null,
      currentArtifactId: null,

      fetchAssignments: async (artifactId: string) => {
        set((state) => ({
          isLoading: true,
          error: null,
          currentArtifactId: artifactId,
          // Switching documents drops the previous one's rows immediately.
          // Without this they stay in the store for the length of the request,
          // and the new document renders the old document's section statuses
          // and progress rollup. Refetching the same document keeps its rows so
          // the page doesn't flicker back through an empty state.
          assignments: state.currentArtifactId === artifactId ? state.assignments : [],
        }))
        try {
          const data = await api.get<{ assignments: SectionAssignment[] }>(
            `/artifacts/${artifactId}/assignments`
          )
          // A different document may have been requested while this was in
          // flight — it owns the store now, so a late response must not land
          // its rows on top of the newer one.
          if (get().currentArtifactId !== artifactId) return
          set({ assignments: data.assignments, isLoading: false })
        } catch (error) {
          if (get().currentArtifactId !== artifactId) return
          set({
            error: error instanceof Error ? error.message : 'Failed to load assignments',
            isLoading: false,
          })
        }
      },

      assignSection: async (artifactId: string, sectionKey: string, userId: string) => {
        const prev = get().assignments
        const previousRow = prev.find((a) => a.sectionId === sectionKey)

        // Optimistic: remove old assignment for this section, add new one
        const optimistic: SectionAssignment = {
          id: `temp-${Date.now()}`,
          bidId: '',
          sectionId: sectionKey,
          userId,
          role: 'writer',
          assignedBy: null,
          collaborationStatus: 'not_started',
          reviewerId: null,
          reviewRounds: [],
          createdAt: new Date().toISOString(),
          collaborationStatusChangedAt: new Date().toISOString(),
          email: '',
          fullName: null,
          avatarUrl: null,
          // The server carries the plan across an owner change; so does the optimistic row.
          plannedReviewerId: previousRow?.plannedReviewerId ?? null,
          plannedReviewerName: previousRow?.plannedReviewerName ?? null,
        }
        set({
          assignments: [
            ...prev.filter((a) => a.sectionId !== sectionKey),
            optimistic,
          ],
        })

        try {
          const data = await api.post<{ assignment: SectionAssignment }>(
            `/artifacts/${artifactId}/sections/${encodeURIComponent(sectionKey)}/assign`,
            { userId }
          )
          // Replace optimistic with server response
          set({
            assignments: [
              ...get().assignments.filter((a) => a.sectionId !== sectionKey),
              data.assignment,
            ],
          })
        } catch (error) {
          // Rollback
          set({ assignments: prev })
          throw error
        }
      },

      unassignSection: async (artifactId: string, sectionKey: string) => {
        const prev = get().assignments

        // Optimistic: clear user but keep status-only row
        set({
          assignments: prev.map((a) =>
            a.sectionId === sectionKey
              ? { ...a, userId: null, assignedBy: null, email: null, fullName: null, avatarUrl: null }
              : a
          ),
        })

        try {
          await api.delete(
            `/artifacts/${artifactId}/sections/${encodeURIComponent(sectionKey)}/assign`
          )
        } catch (error) {
          // Rollback
          set({ assignments: prev })
          throw error
        }
      },

      planReviewer: async (artifactId: string, sectionKey: string, userId: string | null) => {
        const prev = get().assignments
        const existing = prev.find((a) => a.sectionId === sectionKey)

        if (existing) {
          set({
            assignments: prev.map((a) =>
              a.sectionId === sectionKey
                ? { ...a, plannedReviewerId: userId, plannedReviewerName: null }
                : a
            ),
          })
        } else if (userId) {
          // The server creates a status-only row to carry the plan.
          const now = new Date().toISOString()
          const optimistic: SectionAssignment = {
            id: `temp-plan-${Date.now()}`,
            bidId: '',
            sectionId: sectionKey,
            userId: null,
            role: 'writer',
            assignedBy: null,
            collaborationStatus: 'not_started',
            reviewerId: null,
            reviewRounds: [],
            createdAt: now,
            collaborationStatusChangedAt: now,
            email: null,
            fullName: null,
            avatarUrl: null,
            plannedReviewerId: userId,
            plannedReviewerName: null,
          }
          set({ assignments: [...prev, optimistic] })
        }

        try {
          const data = await api.post<{ assignment: SectionAssignment | null }>(
            `/artifacts/${artifactId}/sections/${encodeURIComponent(sectionKey)}/assign`,
            { plannedReviewerId: userId }
          )
          const { assignment } = data
          if (assignment) {
            set({
              assignments: [
                ...get().assignments.filter((a) => a.sectionId !== sectionKey),
                assignment,
              ],
            })
          }
        } catch (error) {
          set({ assignments: prev })
          throw error
        }
      },

      updateCollaborationStatus: async (artifactId: string, sectionKey: string, status: CollaborationStatus) => {
        const prev = get().assignments
        const existing = prev.find((a) => a.sectionId === sectionKey)

        if (existing) {
          // Optimistic update of existing entry
          set({
            assignments: prev.map((a) =>
              a.sectionId === sectionKey
                ? { ...a, collaborationStatus: status }
                : a
            ),
          })
        } else {
          // Optimistic: create a status-only entry for unassigned section
          const optimistic: SectionAssignment = {
            id: `temp-status-${Date.now()}`,
            bidId: '',
            sectionId: sectionKey,
            userId: null,
            role: 'writer',
            assignedBy: null,
            collaborationStatus: status,
            reviewerId: null,
            reviewRounds: [],
            createdAt: new Date().toISOString(),
            collaborationStatusChangedAt: new Date().toISOString(),
            email: null,
            fullName: null,
            avatarUrl: null,
          }
          set({ assignments: [...prev, optimistic] })
        }

        try {
          const data = await api.patch<{ assignment: SectionAssignment }>(
            `/artifacts/${artifactId}/sections/${encodeURIComponent(sectionKey)}/collaboration-status`,
            { status }
          )
          // Replace optimistic with server response
          set({
            assignments: get().assignments.map((a) =>
              a.sectionId === sectionKey ? data.assignment : a
            ),
          })
        } catch (error) {
          // Rollback
          set({ assignments: prev })
          throw error
        }
      },

      requestReview: async (artifactId: string, sectionKey: string, reviewerId: string) => {
        const prev = get().assignments

        // Optimistic update
        set({
          assignments: prev.map((a) =>
            a.sectionId === sectionKey
              ? { ...a, collaborationStatus: 'in_review' as CollaborationStatus, reviewerId }
              : a
          ),
        })

        try {
          const data = await api.post<{ assignment: SectionAssignment }>(
            `/artifacts/${artifactId}/sections/${encodeURIComponent(sectionKey)}/request-review`,
            { reviewerId }
          )
          set({
            assignments: get().assignments.map((a) =>
              a.sectionId === sectionKey ? data.assignment : a
            ),
          })
        } catch (error) {
          set({ assignments: prev })
          throw error
        }
      },

      submitReview: async (artifactId: string, sectionKey: string, decision: 'approved' | 'changes_requested', comment?: string, options?: { requireReReview?: boolean }) => {
        const prev = get().assignments

        // Optimistic update
        const newStatus: CollaborationStatus = decision === 'approved' ? 'approved' : 'changes_requested'
        set({
          assignments: prev.map((a) =>
            a.sectionId === sectionKey
              ? { ...a, collaborationStatus: newStatus }
              : a
          ),
        })

        try {
          const data = await api.post<{ assignment: SectionAssignment }>(
            `/artifacts/${artifactId}/sections/${encodeURIComponent(sectionKey)}/review`,
            { decision, ...(comment ? { comment } : {}), ...(options?.requireReReview !== undefined ? { requireReReview: options.requireReReview } : {}) }
          )
          set({
            assignments: get().assignments.map((a) =>
              a.sectionId === sectionKey ? data.assignment : a
            ),
          })
        } catch (error) {
          set({ assignments: prev })
          throw error
        }
      },

      getAssignment: (sectionKey: string) => {
        return get().assignments.find((a) => a.sectionId === sectionKey)
      },

      reset: () => {
        set({
          assignments: [],
          isLoading: false,
          error: null,
          currentArtifactId: null,
        })
      },
    }),
    { name: 'section-assignments' }
  )
)
